import ee, { ensureEarthEngineReady } from '../../services/earth-engine';
import { getSelectedSatellite, REDUCERS, stripSuffix } from '../utils/index.js';
import { Lyzenga, addpSDB } from './index.js';
import { Actions as Acquisition } from '../../store/ducks/acquisition';
import { aoiGeometry } from '../../common/aoi';
import { trainingCollection } from './depthImage';

export const SESSION_EXPORT_VERSION = 1;
export const SESSION_EXPORT_MODULE = 'bathymetry';

/**
 * @function smoothBandsWater
 * @description Duplicated from `PeriodChooser.jsx` (not exported there) — smooths the given
 * bands over the water mask with a Gaussian kernel, exactly as the composite pipeline does
 * before storing the final composite image.
 * @param {ee.Image} img - Image already carrying a `waterMask_any` band.
 * @param {Array<String>} bands - Band names to smooth.
 * @param {Number} radiusPx - Kernel radius, in pixels.
 * @param {Number} smoothSigma - Gaussian kernel standard deviation.
 * **Called by:
 * @see {importSessionState} - composite-image reconstruction branch.
 * @memberof module:Bathymetry
 * @returns {ee.Image} The image with smoothed bands.
 */
const smoothBandsWater = (img, bands, radiusPx, smoothSigma) => {
  const waterMask = img.select('waterMask_any');
  const gaussianKernel = ee.Kernel.gaussian({ sigma: smoothSigma, radius: radiusPx, units: 'pixels', normalize: true });
  const smoothed = img
    .select(bands)
    .reduceNeighborhood({ reducer: ee.Reducer.mean(), kernel: gaussianKernel, skipMasked: true })
    .rename(bands)
    .updateMask(waterMask);
  return img.addBands(smoothed, bands, true);
};

/**
 * @function reducePseudoFromCollection
 * @description Duplicated from `BathymetryBandsAndAlgorithmSelection.jsx` (not exported there) —
 * computes pseudo-bathymetry bands per image in a collection and reduces them with the 90th
 * percentile, used as an extra predictor band for composite images under Stumpf/Caballero.
 * @param {ee.ImageCollection} col - Source image collection.
 * @param {Array<String>} bands - `[blue, green, red]` band names for the current mission.
 * @param {Number} bdsModel - `2` (Stumpf 2003) or `3` (Caballero & Stumpf 2020).
 * **Called by:
 * @see {importSessionState} - non-preset, composite-image sampled-image reconstruction.
 * @memberof module:Bathymetry
 * @returns {ee.Image} Reduced image containing the pseudo-bathymetry band(s).
 */
const reducePseudoFromCollection = (col, bands, bdsModel = 2) => {
  const withP = col.map((img) => {
    if (bdsModel === 2) {
      img = img.addBands(addpSDB(img, bands));
      return img
        .select(ee.List(['pSDB']))
        .toFloat()
        .copyProperties(img, img.propertyNames());
    }
    const greenBand = addpSDB(img, [bands[0], bands[1]], 'pSDBgreen');
    const redBand = addpSDB(img, [bands[0], bands[2]], 'pSDBred');
    img = img.addBands([greenBand, redBand]);
    return img
      .select(ee.List(['pSDBgreen', 'pSDBred']))
      .toFloat()
      .copyProperties(img, img.propertyNames());
  });
  const outP = withP.reduce(ee.Reducer.percentile([90]));
  const suf = ee.String('_p').cat(ee.Number(90).format());
  return bdsModel === 2
    ? outP.select([ee.String('pSDB').cat(suf)], ['pSDB'])
    : outP.select([ee.String('pSDBgreen').cat(suf), ee.String('pSDBred').cat(suf)], ['pSDBgreen', 'pSDBred']);
};

/**
 * @function buildSessionExport
 * @description Bundles the Bathymetry Context state (`data`/`fileData` from `useResult`/`useFile`)
 * needed to replay a completed analysis without the user re-clicking through the wizard. Unlike
 * Shoreline, Bathymetry's final result is a live raster (`getThumbURL`, tied to an `ee.Image`
 * computation graph) with no plain-JSON equivalent — so this exports *configuration*, not a
 * frozen result: satellite/AOI, model/coefficients, training data (as its raw, still-plain
 * `[x, y, depth]` matrix), tide config, and everything `PeriodChooser` needs to rebuild the same
 * working image. `data.image`/`data.sampledImage` (live `ee.Image`/`ee.FeatureCollection`) are
 * deliberately left out — `importSessionState` reconstructs them by replaying the same Earth
 * Engine calls the wizard steps would have made.
 * @param {Object} params
 * @param {Object} params.data - `useResult()`'s `data`.
 * @param {Object} params.fileData - `useFile()`'s `fileData`.
 * **Called by:
 * @see {ProcessingPage} - Bathymetry's result page, via the "export session for testing" button.
 * @memberof module:Bathymetry
 * @returns {Object} A plain, JSON-serializable session snapshot.
 */
export const buildSessionExport = ({ data, fileData }) => {
  const periodState = fileData?.periodState || {};
  const compositeImageList = periodState.compositeImageList || [];
  const selectedCompositeImages = periodState.selectedCompositeImages || {};
  return {
    version: SESSION_EXPORT_VERSION,
    module: SESSION_EXPORT_MODULE,
    exportedAt: new Date().toISOString(),
    satelliteIndex: Number.parseInt(window.sessionStorage.getItem('selectedSatelliteIndex'), 10),
    satelliteType: window.sessionStorage.getItem('typeSatellite') || 'TOA',
    coordinates: fileData?.coordinates,
    coordinatesSystem: fileData?.coordinatesSystem,
    threshold: fileData?.threshold,
    waterMaskMethod: fileData?.waterMaskMethod,
    trainingDate: fileData?.date,
    timezoneOffset: fileData?.timezoneOffset,
    kmlFileName: fileData?.kmlFileName,
    trainingMatrix: fileData?.trainingMatrix,
    tideData: fileData?.tideData,
    tideFileName: fileData?.tideFileName,
    tideConfig: data?.tideConfig,
    presetMode: data?.presetMode,
    presetCoeficients: data?.presetCoeficients,
    presetContext: data?.presetContext,
    modelPresetSelection: data?.modelPresetSelection,
    coefficientValuesRaw: data?.coefficientValuesRaw,
    trainingDataReducer: data?.trainingDataReducer,
    validationSplit: data?.validationSplit,
    treeParams: data?.treeParams,
    landsatChosenCollections: data?.landsatChosenCollections,
    modelSelected: data?.modelSelected,
    bands: data?.bands,
    rgb: data?.rgb,
    thresholdValue: data?.thresholdValue,
    depthLightExtinction: data?.depthLightExtinction,
    imageDate: data?.imageDate,
    typeImageProcess: data?.typeImageProcess,
    selectedImageDates: data?.selectedImageDates,
    missionInfo: data?.missionInfo,
    period: periodState.period,
    reducerType: periodState.reducerType,
    applyMosaicComposite: periodState.applyMosaicComposite,
    applyMosaicSingle: periodState.applyMosaicSingle,
    smoothRadius: periodState.smoothRadius,
    smoothSigma: periodState.smoothSigma,
    selectedCompositeImageIds: compositeImageList.filter((img) => selectedCompositeImages[img.id]).map((img) => img.id),
  };
};

/**
 * @function importSessionState
 * @description Replays the Earth Engine calls `AOIChooser`/`PeriodChooser`/
 * `BathymetryBandsAndAlgorithmSelection` would have made for the exported configuration, then
 * drops the result into `data`/`fileData` Context in exactly the shape those steps leave behind
 * on `performChoose`/`handleNext`/`handleFinish`. Once `data.image`, `roiRef.current`,
 * `data.bands`, `fileData.coordinatesSystem`, and `data.missionInfo` are all set, the result
 * page's own effect (unchanged) takes over automatically: fits/reads coefficients, applies the
 * tide correction, and calls `getThumbURL` — this function never calls `getThumbURL` itself.
 * This genuinely re-runs Earth Engine (unlike Shoreline's import, which needs none): the working
 * image has to be rebuilt from the raw satellite collection, and — for non-preset analyses — the
 * training sample has to be re-extracted against that rebuilt image. What's skipped is the
 * *wizard*, not the computation: no manual AOI redraw, no re-uploading training/tide files, no
 * re-picking bands/model, no re-running the composite reducer UI.
 * @param {Object} ctx - Dependencies sourced from hooks (can't be imported directly since these
 * are React Context values, not module state).
 * @param {Function} ctx.dispatch - Redux dispatch.
 * @param {Function} ctx.saveProcessingCollectionValues - From `usePreProcessing()`.
 * @param {Object} ctx.waterMaskMethodRef - From `usePreProcessing()`.
 * @param {Function} ctx.generateCollection - From `usePreProcessing()`.
 * @param {Function} ctx.cleanAndMaskImages - From `usePreProcessing()`.
 * @param {Function} ctx.createMosaicByCommonId - From `usePreProcessing()`.
 * @param {Object} ctx.collectionRef - From `usePreProcessing()`.
 * @param {Function} ctx.setData - From `useResult()`.
 * @param {Function} ctx.setFileData - From `useFile()`.
 * @param {Object} exported - A session snapshot produced by `buildSessionExport`.
 * @see {@link buildSessionExport}
 * **Called by:
 * @see {Bathymetry} - via the "import session for testing" file input.
 * @memberof module:Bathymetry
 * @returns {Promise<void>}
 */
export const importSessionState = async (ctx, exported) => {
  await ensureEarthEngineReady();
  const {
    dispatch,
    saveProcessingCollectionValues,
    waterMaskMethodRef,
    generateCollection,
    cleanAndMaskImages,
    createMosaicByCommonId,
    collectionRef,
    setData,
    setFileData,
  } = ctx;
  const {
    satelliteIndex,
    satelliteType,
    coordinates,
    coordinatesSystem,
    threshold,
    waterMaskMethod,
    trainingDate,
    timezoneOffset,
    kmlFileName,
    trainingMatrix,
    tideData,
    tideFileName,
    tideConfig,
    presetMode,
    presetCoeficients,
    presetContext,
    modelPresetSelection,
    coefficientValuesRaw,
    trainingDataReducer,
    validationSplit,
    treeParams,
    landsatChosenCollections,
    modelSelected,
    bands,
    rgb,
    thresholdValue,
    depthLightExtinction,
    imageDate,
    typeImageProcess,
    selectedImageDates,
    missionInfo,
    period,
    reducerType,
    applyMosaicComposite,
    applyMosaicSingle,
    smoothRadius,
    smoothSigma,
    selectedCompositeImageIds,
  } = exported;

  dispatch(Acquisition.reset());
  dispatch(Acquisition.setSatellite(satelliteIndex, satelliteType));
  const satellite = getSelectedSatellite();
  const satelliteInfo = satelliteType === 'TOA' ? satellite.missions : satellite.surfaceReflectance;
  const roi = aoiGeometry(coordinates);
  dispatch(Acquisition.setAOI(null, coordinates, roi, kmlFileName));
  saveProcessingCollectionValues(satellite, `${satelliteInfo[0].startYear}-01-01`, roi, threshold);
  waterMaskMethodRef.current = waterMaskMethod;

  const matrix = trainingMatrix?.length ? trainingCollection(trainingMatrix, coordinatesSystem) : null;

  let image;
  if (typeImageProcess === 'COMPOSITE' && period?.start && period?.end) {
    await generateCollection(period.start, period.end, landsatChosenCollections);
    await cleanAndMaskImages(satelliteType === 'TOA', undefined, satelliteInfo);
    collectionRef.current = applyMosaicComposite ? createMosaicByCommonId() : collectionRef.current;
    const windowed = collectionRef.current.filterDate(period.start, period.end).filterBounds(roi);
    let col = windowed;
    if (selectedCompositeImageIds?.length) {
      col = col.filter(ee.Filter.or(...selectedCompositeImageIds.map((id) => ee.Filter.eq('system:index', id))));
    }
    const reducerDef = REDUCERS[reducerType];
    let reducedImage;
    if (reducerDef?.label === 'geomed') {
      const first = col.first();
      const log = col.map((img) => img.toFloat().max(1e-3).log());
      reducedImage = ee.Image(
        log
          .reduce(ee.Reducer.mean())
          .toFloat()
          .regexpRename('(.*)_mean', '$1')
          .exp()
          .toFloat()
          .copyProperties(first, first.propertyNames()),
      );
    } else {
      reducedImage = stripSuffix(col.reduce(reducerDef.func()), `_${reducerDef.label}`);
    }
    const waterAny = windowed.select('waterMask_final').reduce(ee.Reducer.max()).rename('waterMask_any');
    const imageReducedWater = ee.Image(reducedImage).addBands(waterAny, null, true);
    const coreBands = [
      missionInfo.bands.blue,
      missionInfo.bands.green,
      missionInfo.bands.red,
      missionInfo.bands.nir,
      missionInfo.bands.swir,
    ].filter(Boolean);
    const optionalBands = ['swir2', 'nir2'].map((b) => missionInfo.bands?.[b]).filter(Boolean);
    image = smoothBandsWater(
      imageReducedWater,
      [...coreBands, ...optionalBands],
      smoothRadius ?? 1,
      smoothSigma ?? 0.1,
    );
  } else {
    const targetDate = ee.Date(trainingDate);
    await generateCollection(targetDate.advance(-1, 'year'), targetDate.advance(1, 'year'), landsatChosenCollections);
    await cleanAndMaskImages(satelliteType === 'TOA', undefined, satelliteInfo);
    collectionRef.current = applyMosaicSingle ? createMosaicByCommonId() : collectionRef.current;
    image = collectionRef.current.first();
  }

  const thumbImage = image.getThumbURL({
    region: roi,
    bands: [missionInfo.bands.red, missionInfo.bands.green, missionInfo.bands.blue],
    dimensions: 512,
    max: 0.2,
    min: 0.0,
    format: 'png',
  });

  let sampledImage = null;
  if (!presetMode && matrix) {
    const scale = missionInfo?.opticalResolution || 30;
    const sample = (img, sampleBands) =>
      img
        .select(sampleBands)
        .clip(roi)
        .sampleRegions({
          collection: matrix,
          properties: ['or_depth'],
          scale,
          geometries: false,
          tileScale: 4,
        });
    if (modelSelected === 'Lyzenga') {
      const predictorImage = Lyzenga.add_lyzengaPredictors(image, bands).addBands(
        ee.Image.constant(1).rename('constant'),
        null,
        true,
      );
      sampledImage = sample(predictorImage, ['lnBand1', 'lnBand2', 'lnBand3', 'constant']);
    } else {
      let workingImage = image;
      if (typeImageProcess === 'COMPOSITE') {
        const bdsModel = modelSelected === 'Caballero & Stumpf 2020' ? 3 : 2;
        workingImage = ee
          .Image(workingImage)
          .addBands(reducePseudoFromCollection(collectionRef.current, bands, bdsModel), null, true);
      }
      if (modelSelected === 'Stumpf 2003') {
        sampledImage = sample(workingImage.addBands(addpSDB(workingImage, bands)), ['pSDB']);
      } else {
        const bandGreen = addpSDB(workingImage, [bands[0], bands[1]], 'pSDBgreen');
        const bandRed = addpSDB(workingImage, [bands[0], bands[2]], 'pSDBred');
        sampledImage = sample(workingImage.addBands([bandGreen, bandRed]), ['pSDBgreen', 'pSDBred']);
      }
    }
  }

  console.warn('[Bathymetry] importSessionState: about to set Context, has:', {
    image: !!image,
    bands: bands?.length,
    coordinatesSystem: !!coordinatesSystem,
    missionInfo: !!missionInfo,
    modelSelected,
    presetMode,
    sampledImage: !!sampledImage,
  });

  setFileData((prev) => ({
    ...prev,
    coordinatesSystem,
    date: trainingDate,
    timezoneOffset,
    coordinates,
    threshold,
    waterMaskMethod,
    kmlFileName,
    trainingMatrix,
    matrix,
    tideData,
    tideFileName,
  }));
  setData((prev) => ({
    ...prev,
    trainingDataReducer,
    validationSplit,
    treeParams,
    presetCoeficients,
    presetContext,
    presetMode,
    modelPresetSelection,
    coefficientValuesRaw,
    tideConfig,
    landsatChosenCollections,
    image,
    sampledImage,
    thumbImage,
    bands,
    modelSelected,
    rgb,
    thresholdValue,
    depthLightExtinction,
    imageDate,
    typeImageProcess,
    selectedImageDates,
    missionInfo,
  }));
};
